import dayjs from 'dayjs';
import { GridInitialState } from '@mui/x-data-grid-pro';

import { User, ReportLevel } from 'types';

const userKey = 'authUser';
const darkThemeKey = 'isDarkTheme';
const reportsFormKey = 'reportsForm';
const reportsDataKey = 'reportsData';
const showIdentityKey = 'showIdentity';
const useQuestionCodesAsHeadersKey = 'useQuestionCodesAsHeaders';
export const LIBRARY_TENANT_STORAGE_KEY = 'libraryTenantId';
export const REMEMBERED_SSO_IDENTIFIER_STORAGE_KEY = 'rememberedSsoIdentifier';
const rememberedEmailKey = 'rememberedEmail';
const authStageKey = 'authStage';
const sessionIdStorageKey = 'session_id';
const allow404StorageKey = 'allow-404';
const ssoStateNonceStorageKey = 'ssoStateNonce';
const verifyPageActiveStorageKey = 'verifyPageActive';
const pdfManagementTenantStorageKey = 'pdfManagementTenantId';

export const SELECTED_PROJECT_STORAGE_KEY = 'selectedProjectId';

export const ACCESS_TOKEN_STORAGE_KEY = 'accessToken';
export const REFRESH_TOKEN_STORAGE_KEY = 'refreshToken';
export const LOCAL_STORAGE_CHANGE_EVENT = 'local-storage-change';

export type TokenKey =
  typeof ACCESS_TOKEN_STORAGE_KEY | typeof REFRESH_TOKEN_STORAGE_KEY;
export type AuthStage = 'mfa' | 'authenticated';

export interface Tokens {
  accessToken?: string;
  refreshToken?: string;
}

const tokenKeys: TokenKey[] = [
  ACCESS_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
];
const auth0IdKey = 'auth0Id';

export const getLocalStorageItem = (key: string): string | null =>
  localStorage.getItem(key);

export const setLocalStorageItem = (key: string, value: string): void => {
  localStorage.setItem(key, value);
  window.dispatchEvent(
    new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
      detail: { key, value },
    })
  );
};

export const removeLocalStorageItem = (key: string): void => {
  localStorage.removeItem(key);
  window.dispatchEvent(
    new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
      detail: { key, value: null },
    })
  );
};

export const getSessionStorageItem = (key: string): string | null =>
  sessionStorage.getItem(key) ?? null;

export const setSessionStorageItem = (key: string, value: string): void => {
  sessionStorage.setItem(key, value);
};

export const removeSessionStorageItem = (key: string): void => {
  sessionStorage.removeItem(key);
};

export const saveSsoStateNonce = (nonce: string) =>
  setSessionStorageItem(ssoStateNonceStorageKey, nonce);

export const getSsoStateNonce = (): string | null =>
  getSessionStorageItem(ssoStateNonceStorageKey);

export const clearSsoStateNonce = () =>
  removeSessionStorageItem(ssoStateNonceStorageKey);

export const setVerifyPageActive = () =>
  setSessionStorageItem(verifyPageActiveStorageKey, 'true');

export const wasVerifyPageActive = () =>
  getSessionStorageItem(verifyPageActiveStorageKey) === 'true';

export const clearVerifyPageActive = () =>
  removeSessionStorageItem(verifyPageActiveStorageKey);

export const getOrCreateSessionId = (): string => {
  const existingSessionId = getLocalStorageItem(sessionIdStorageKey);

  if (existingSessionId) {
    return existingSessionId;
  }

  const newSessionId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `sid_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

  setLocalStorageItem(sessionIdStorageKey, newSessionId);

  return newSessionId;
};

export const clearSessionId = () => removeLocalStorageItem(sessionIdStorageKey);

export const is404Allowed = () =>
  Boolean(getLocalStorageItem(allow404StorageKey));

export const saveReportsForm = (forms: Record<string, any>) =>
  setLocalStorageItem(reportsFormKey, JSON.stringify(forms));

export const getReportsForm = () => {
  const stored = getLocalStorageItem(reportsFormKey);
  if (!stored) return null;

  const parsedValues = JSON.parse(stored);

  if (!parsedValues.level) {
    parsedValues.level = ReportLevel.ORGANIZATION;
  }

  if (!('projectId' in parsedValues)) {
    parsedValues.projectId = '';
  }

  if (parsedValues.reports && Array.isArray(parsedValues.reports)) {
    parsedValues.reports = parsedValues.reports.map((report: any) => ({
      ...report,
      projectId: report.projectId || '',
      to: report.to ? dayjs(report.to) : null,
      projectReportId: report.projectReportId || '',
      from: report.from ? dayjs(report.from) : null,
    }));
  }

  return parsedValues;
};

export const clearReportsForm = () => removeLocalStorageItem(reportsFormKey);

export const saveReportsData = (reports: Record<string, any>) =>
  setLocalStorageItem(reportsDataKey, JSON.stringify(reports));

export const getReportsData = () => {
  const stored = getLocalStorageItem(reportsDataKey);
  return stored ? JSON.parse(stored) : null;
};

export const clearReportsData = () => removeLocalStorageItem(reportsDataKey);

export const rememberEmail = (remember: boolean, email?: string) => {
  if (remember && email) {
    setLocalStorageItem(rememberedEmailKey, email);
  } else {
    removeLocalStorageItem(rememberedEmailKey);
  }
};

export const getRememberedEmail = () => getLocalStorageItem(rememberedEmailKey);

export const saveDarkTheme = (isDarkTheme?: boolean) => {
  if (typeof isDarkTheme === 'boolean') {
    setLocalStorageItem(darkThemeKey, String(isDarkTheme));
  } else {
    removeLocalStorageItem(darkThemeKey);
  }
};

export const getIsDarkTheme = (): boolean | undefined => {
  const isDarkTheme = getLocalStorageItem(darkThemeKey);
  if (isDarkTheme === null) {
    return undefined;
  }
  return isDarkTheme === 'true';
};

export const clearTokens = () =>
  tokenKeys.forEach((tokenKey) => removeLocalStorageItem(tokenKey));

export const clearUser = () => removeLocalStorageItem(userKey);

export const saveShowIdentity = (showIdentity: boolean) =>
  setLocalStorageItem(showIdentityKey, String(showIdentity));

export const getShowIdentity = (defaultValue = false): boolean => {
  const showIdentity = getLocalStorageItem(showIdentityKey);
  if (showIdentity === null) {
    return defaultValue;
  }
  return showIdentity === 'true';
};

export const saveLibraryTenantId = (tenantId: string) =>
  setLocalStorageItem(LIBRARY_TENANT_STORAGE_KEY, tenantId);

export const getLibraryTenantId = () =>
  getLocalStorageItem(LIBRARY_TENANT_STORAGE_KEY);

export const saveUseQuestionCodesAsHeaders = (
  useQuestionCodesAsHeaders: boolean
) =>
  setLocalStorageItem(
    useQuestionCodesAsHeadersKey,
    String(useQuestionCodesAsHeaders)
  );

export const getUseQuestionCodesAsHeaders = (): boolean | undefined => {
  const value = getLocalStorageItem(useQuestionCodesAsHeadersKey);
  return value === null ? undefined : value === 'true';
};

export const clearUseQuestionCodesAsHeaders = () =>
  removeLocalStorageItem(useQuestionCodesAsHeadersKey);

export const clearShowIdentity = () => removeLocalStorageItem(showIdentityKey);
export const clearLibraryTenantId = () =>
  removeLocalStorageItem(LIBRARY_TENANT_STORAGE_KEY);

export const clearSelectedProjectId = () =>
  removeLocalStorageItem(SELECTED_PROJECT_STORAGE_KEY);

export const savePdfManagementTenantId = (tenantId: string) =>
  setLocalStorageItem(pdfManagementTenantStorageKey, tenantId);

export const getPdfManagementTenantId = () =>
  getLocalStorageItem(pdfManagementTenantStorageKey);

export const clearPdfManagementTenantId = () =>
  removeLocalStorageItem(pdfManagementTenantStorageKey);

export const clearAll = () => {
  clearTokens();
  clearUser();
  clearReportsData();
  clearReportsForm();
  clearShowIdentity();
  clearUseQuestionCodesAsHeaders();
  clearLibraryTenantId();
  clearAuthStage();
  clearAuth0Id();
  clearSelectedProjectId();
  clearPdfManagementTenantId();
};

export const saveRememberedSsoIdentifier = (identifier: string) =>
  setLocalStorageItem(REMEMBERED_SSO_IDENTIFIER_STORAGE_KEY, identifier);

export const getRememberedSsoIdentifier = () =>
  getLocalStorageItem(REMEMBERED_SSO_IDENTIFIER_STORAGE_KEY);

export const clearRememberedSsoIdentifier = () =>
  removeLocalStorageItem(REMEMBERED_SSO_IDENTIFIER_STORAGE_KEY);

export const saveToken = (tokenKey: TokenKey, token: string) =>
  setLocalStorageItem(tokenKey, token);

export const saveTokens = ({ accessToken, refreshToken }: Tokens) => {
  if (accessToken) {
    saveToken('accessToken', accessToken);
  }
  if (refreshToken) {
    saveToken('refreshToken', refreshToken);
  }
};

export const getToken = (tokenKey: TokenKey = ACCESS_TOKEN_STORAGE_KEY) => {
  return getLocalStorageItem(tokenKey) || undefined;
};

export const getAccessToken = () => getToken(ACCESS_TOKEN_STORAGE_KEY);

export const getRefreshToken = () => getToken(REFRESH_TOKEN_STORAGE_KEY);

export const saveUser = (user: User) =>
  setLocalStorageItem(userKey, JSON.stringify(user));

export const getUser = (): User | undefined => {
  const user = getLocalStorageItem(userKey);
  if (!user) {
    return undefined;
  }
  return JSON.parse(user);
};

export const saveAuth0Id = (auth0Id: string) =>
  setLocalStorageItem(auth0IdKey, auth0Id);

export const getAuth0Id = () => getLocalStorageItem(auth0IdKey) as string;

export const clearAuth0Id = () => removeLocalStorageItem(auth0IdKey);

export const saveAuthStage = (authStage: AuthStage) =>
  setLocalStorageItem(authStageKey, authStage);

export const getAuthStage = (): AuthStage | undefined => {
  const authStage = getLocalStorageItem(authStageKey);

  if (authStage === 'mfa' || authStage === 'authenticated') {
    return authStage;
  }

  return undefined;
};

export const clearAuthStage = () => removeLocalStorageItem(authStageKey);

export const getTokens = () => ({
  accessToken: getAccessToken(),
  refreshToken: getRefreshToken(),
});

type SavedGridState = {
  version: number;
  state: GridInitialState;
};

export const loadGridState = (
  storageKey: string,
  version?: number
): SavedGridState | null => {
  try {
    const raw = getLocalStorageItem(storageKey);
    if (!raw) return null;

    const saved = JSON.parse(raw);
    if (
      !saved ||
      typeof saved !== 'object' ||
      Array.isArray(saved) ||
      !saved.state ||
      typeof saved.state !== 'object' ||
      Array.isArray(saved.state)
    ) {
      removeLocalStorageItem(storageKey);
      return null;
    }

    if (version !== undefined && saved.version !== version) {
      removeLocalStorageItem(storageKey);
      return null;
    }

    return saved;
  } catch (error) {
    console.error('[GridStateStorage] Failed to load state:', error);
    return null;
  }
};

export const saveGridState = (
  storageKey: string,
  state: GridInitialState,
  version: number = 1
): void => {
  try {
    const saved = { state, version };
    setLocalStorageItem(storageKey, JSON.stringify(saved));
  } catch (error) {
    console.error('[GridStateStorage] Failed to save state:', error);
  }
};

export const cleanupOldGridStates = (
  prefix: string,
  currentKey: string
): void => {
  try {
    const keysToDelete: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix) && key !== currentKey) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => removeLocalStorageItem(key));
  } catch (error) {
    console.error('[GridStateStorage] Failed to cleanup old states:', error);
  }
};
